import React, { useState, useEffect } from 'react';
import { X, Search, Sparkles, Zap, ShieldCheck, Tag, Globe, CreditCard, RefreshCw } from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { 
  CATEGORY_MAP, 
  Language, 
  Currency,
  getLocalizedAgentName, 
  getLocalizedAgentDesc, 
  getLocalizedAgentCategory 
} from '../utils/i18n';

interface PublicPricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialCurrency?: Currency;
  language?: Language;
  currency?: Currency;
}

interface AgentPricing {
  agent_id: string;
  requests_limit: number;
  price: string | number;
  single_price: string | number;
  duration_days?: number;
}

export const PublicPricingModal: React.FC<PublicPricingModalProps> = ({ 
  isOpen, 
  onClose,
  initialCurrency = 'UAH',
  language = 'uk'
}) => {
  const [pricingMap, setPricingMap] = useState<Record<string, AgentPricing>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(initialCurrency);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const isDe = language === 'de';
  const isFr = language === 'fr';
  const isIt = language === 'it';
  const isEs = language === 'es';
  const isPt = language === 'pt';
  const isSv = language === 'sv';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const isUk = language === 'uk' || (!isDe && !isFr && !isIt && !isEs && !isPt && !isSv && !isPl && !isEn);

  useEffect(() => {
    if (!isOpen) return;

    const fetchPricing = async () => {
      setIsLoading(true);
      try {
        const res = await fetch('/api/public/agent-settings');
        if (res.ok) {
          const data: AgentPricing[] = await res.json();
          const map: Record<string, AgentPricing> = {};
          data.forEach(item => {
            map[item.agent_id] = item;
          });
          setPricingMap(map);
        }
      } catch (err) {
        console.error("Failed to fetch public agent settings:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPricing();
  }, [isOpen]);

  if (!isOpen) return null;

  // Filter out system enterprise dashboard from pricing list
  const activeAgents = (agentsConfig as any[]).filter(a => a.id !== 'enterprise-dashboard' && a.id !== 'admin-lead-harvester');

  const categories = ['ALL', ...Array.from(new Set(activeAgents.map(a => a.categoryUk)))];

  const getCategoryLabel = (catKey: string) => {
    if (catKey === 'ALL') {
      return isIt ? 'Tutte le categorie'
        : isEs ? 'Todas las categorías'
        : isPt ? 'Todas as categorias'
        : isSv ? 'Alla kategorier'
        : isDe ? 'Alle Kategorien'
        : isFr ? 'Toutes les catégories'
        : isPl ? 'Wszystkie kategorie'
        : isEn ? 'All Categories'
        : 'Всі категорії';
    }
    const entry = CATEGORY_MAP[catKey];
    if (entry) {
      return (entry as any)[language] || entry.en || entry.uk;
    }
    return catKey;
  };

  const filteredAgents = activeAgents.filter(agent => {
    const nameUk = (agent.nameUk || '').toLowerCase();
    const nameEn = (agent.nameEn || '').toLowerCase();
    const namePl = (agent.namePl || '').toLowerCase();
    const nameDe = (agent.nameDe || '').toLowerCase();
    const nameFr = (agent.nameFr || '').toLowerCase();
    const nameIt = (agent.nameIt || '').toLowerCase();
    const nameEs = (agent.nameEs || '').toLowerCase();
    const namePt = (agent.namePt || '').toLowerCase();
    const nameSv = (agent.nameSv || '').toLowerCase();
    const descUk = (agent.descriptionUk || '').toLowerCase();
    const descEn = (agent.descriptionEn || '').toLowerCase();
    const descPl = (agent.descriptionPl || '').toLowerCase();
    const descDe = (agent.descriptionDe || '').toLowerCase();
    const descFr = (agent.descriptionFr || '').toLowerCase();
    const descIt = (agent.descriptionIt || '').toLowerCase();
    const descEs = (agent.descriptionEs || '').toLowerCase();
    const descPt = (agent.descriptionPt || '').toLowerCase();
    const descSv = (agent.descriptionSv || '').toLowerCase();
    const catUk = (agent.categoryUk || '').toLowerCase();
    const id = (agent.id || '').toLowerCase();
    const sq = searchQuery.toLowerCase().trim();

    const matchesSearch = !sq || 
                          nameUk.includes(sq) ||
                          nameEn.includes(sq) ||
                          namePl.includes(sq) ||
                          nameDe.includes(sq) ||
                          nameFr.includes(sq) ||
                          nameIt.includes(sq) ||
                          nameEs.includes(sq) ||
                          namePt.includes(sq) ||
                          nameSv.includes(sq) ||
                          descUk.includes(sq) ||
                          descEn.includes(sq) ||
                          descPl.includes(sq) ||
                          descDe.includes(sq) ||
                          descFr.includes(sq) ||
                          descIt.includes(sq) ||
                          descEs.includes(sq) ||
                          descPt.includes(sq) ||
                          descSv.includes(sq) ||
                          catUk.includes(sq) ||
                          id.includes(sq);

    const matchesCategory = selectedCategory === 'ALL' || agent.categoryUk === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatPrice = (uahAmount: number) => {
    if (selectedCurrency === 'USD') {
      const usd = Math.max(1, Math.round(uahAmount / 41.5));
      return `$${usd}`;
    }
    if (selectedCurrency === 'EUR') {
      const eur = Math.max(1, Math.round(uahAmount / 45));
      return `€${eur}`;
    }
    if (selectedCurrency === 'PLN') {
      const pln = Math.max(5, Math.round(uahAmount / 10.5));
      return `${pln} zł`;
    }
    if (selectedCurrency === 'SEK') {
      const sek = Math.max(10, Math.round(uahAmount / 4));
      return `${sek} kr`;
    }
    return `${uahAmount} грн`;
  };

  const formatUnitRate = (uahPerReq: number) => {
    if (selectedCurrency === 'USD') {
      const usd = (uahPerReq / 41.5).toFixed(2);
      return `$${usd}`;
    }
    if (selectedCurrency === 'EUR') {
      const eur = (uahPerReq / 45).toFixed(2);
      return `€${eur}`;
    }
    if (selectedCurrency === 'PLN') {
      const pln = (uahPerReq / 10.5).toFixed(2);
      return `${pln} zł`;
    }
    if (selectedCurrency === 'SEK') {
      const sek = (uahPerReq / 4).toFixed(2);
      return `${sek} kr`;
    }
    return `${uahPerReq.toFixed(2)} грн`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-slate-900/80 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="relative w-full max-w-5xl h-[92vh] sm:h-[90vh] bg-white rounded-2xl shadow-2xl border border-slate-100 flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="p-4 sm:p-6 bg-gradient-to-r from-slate-900 via-[#1E3A8A] to-slate-900 text-white flex items-center justify-between relative overflow-hidden shrink-0">
          <div className="absolute right-0 top-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="space-y-1 z-10">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 bg-blue-500/20 text-blue-200 border border-blue-400/30 rounded-full font-mono text-[10px] uppercase font-bold tracking-wider flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-amber-300" /> 
                {isIt ? 'Tariffe Trasparenti' : isEs ? 'Precios Transparentes' : isPt ? 'Preços Transparentes' : isSv ? 'Transparenta Priser' : isDe ? 'Transparente Tarife' : isFr ? 'Tarification Transparente' : isPl ? 'Przejrzysty Cennik' : isEn ? 'Transparent Pricing' : 'Прозорі Тарифи'}
              </span>
              <span className="px-2.5 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-full font-mono text-[10px] uppercase font-bold">
                🇺🇦 UAH • 🇪🇺 EUR • 🇵🇱 PLN • 🇸🇪 SEK • 🇺🇸 USD
              </span>
            </div>
            <h2 className="text-lg sm:text-xl font-black tracking-tight text-white uppercase">
              {isIt ? 'Catalogo dei Moduli & Tariffe dei Servizi' : isEs ? 'Catálogo de Módulos & Precios de Servicios' : isPt ? 'Catálogo de Módulos & Preços dos Serviços' : isSv ? 'Modulkatalog & Tjänstepriser' : isDe ? 'Katalog der Module & Dienstleistungspreise' : isFr ? 'Catalogue des Modules & Tarifs des Services' : isPl ? 'Katalog Narzędzi & Cennik Usług' : isEn ? 'Tools Catalog & Service Pricing' : 'Каталог Інструментів та Вартість Послуг'}
            </h2>
            <p className="text-[11px] sm:text-xs text-slate-300 font-mono">
              {isIt ? 'Scegli il formato più adatto: abbonamento mensile (30 giorni) o uso a consumo (Pay-per-request)'
                : isEs ? 'Elija el formato más conveniente: suscripción mensual (30 días) o pago por uso (Pay-per-request)'
                : isPt ? 'Escolha o formato conveniente: subscrição mensal (30 dias) ou pagamento avulso (Pay-per-request)'
                : isSv ? 'Välj lämpligt format: månadspaket (30 dagar) eller enstaka körningar (Pay-per-request)'
                : isDe ? 'Wählen Sie das passende Format: Monatliches Abopaket (30 Tage) oder Einzelabruf (Pay-per-Request)'
                : isFr ? 'Choisissez le format adapté : forfait mensuel de requêtes (30 jours) ou utilisation à la demande (Pay-per-request)'
                : isPl ? 'Wybierz abonament miesięczny (30 dni) lub pojedyncze uruchomienie (Pay-per-request)'
                : isEn ? 'Choose monthly subscription package (30 days) or pay-per-request runs'
                : 'Обирайте зручний формат: щомісячна підписка на пакет запитів або разовий запуск (Pay-per-request)'}
            </p>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-full transition-all cursor-pointer z-10"
            title={isIt ? 'Chiudi' : isEs ? 'Cerrar' : isPt ? 'Fechar' : isSv ? 'Stäng' : isDe ? 'Schließen' : isFr ? 'Fermer' : isPl ? 'Zamknij' : isEn ? 'Close' : 'Закрити'}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Toolbar: Currency Switcher & Search */}
        <div className="p-3 sm:p-4 bg-slate-50 border-b border-slate-200 flex flex-col sm:flex-row items-center gap-3 justify-between shrink-0">
          
          {/* Currency Toggle */}
          <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
            <span className="text-[10px] font-bold font-mono text-slate-500 uppercase flex items-center gap-1">
              <Globe className="w-3.5 h-3.5 text-blue-600" />
              <span>{isIt ? 'Valuta:' : isEs ? 'Moneda:' : isPt ? 'Moeda:' : isSv ? 'Valuta:' : isDe ? 'Währung:' : isFr ? 'Devise :' : isPl ? 'Waluta:' : isEn ? 'Currency:' : 'Валюта:'}</span>
            </span>
            <div className="flex items-center bg-white border border-slate-300 rounded-lg p-0.5 font-mono text-xs font-bold shadow-xs flex-wrap">
              <button
                type="button"
                onClick={() => setSelectedCurrency('UAH')}
                className={`px-2.5 py-1 rounded transition-all cursor-pointer ${
                  selectedCurrency === 'UAH' 
                    ? 'bg-blue-600 text-white shadow-xs' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                ₴ UAH
              </button>
              <button
                type="button"
                onClick={() => setSelectedCurrency('USD')}
                className={`px-2.5 py-1 rounded transition-all cursor-pointer ${
                  selectedCurrency === 'USD' 
                    ? 'bg-blue-600 text-white shadow-xs' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                $ USD
              </button>
              <button
                type="button"
                onClick={() => setSelectedCurrency('EUR')}
                className={`px-2.5 py-1 rounded transition-all cursor-pointer ${
                  selectedCurrency === 'EUR' 
                    ? 'bg-blue-600 text-white shadow-xs' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                € EUR
              </button>
              <button
                type="button"
                onClick={() => setSelectedCurrency('PLN')}
                className={`px-2.5 py-1 rounded transition-all cursor-pointer ${
                  selectedCurrency === 'PLN' 
                    ? 'bg-blue-600 text-white shadow-xs' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                zł PLN
              </button>
              <button
                type="button"
                onClick={() => setSelectedCurrency('SEK')}
                className={`px-2.5 py-1 rounded transition-all cursor-pointer ${
                  selectedCurrency === 'SEK' 
                    ? 'bg-blue-600 text-white shadow-xs' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                kr SEK
              </button>
            </div>
          </div>

          {/* Search Input */}
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isIt ? "Cerca modulo..." : isEs ? "Buscar módulo..." : isPt ? "Pesquisar módulo..." : isSv ? "Sök modul..." : isDe ? "Modul suchen..." : isFr ? "Rechercher un module..." : isPl ? "Szukaj narzędzia..." : isEn ? "Search tool..." : "Пошук інструменту..."}
              className="w-full pl-9 pr-4 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-mono focus:outline-none focus:border-[#1E3A8A] transition-all"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Categories pill scroll */}
          <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0 scrollbar-none touch-pan-x">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-bold font-mono whitespace-nowrap transition-all cursor-pointer ${
                  selectedCategory === cat
                    ? 'bg-[#1E3A8A] text-white shadow-sm'
                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                }`}
              >
                {getCategoryLabel(cat)}
              </button>
            ))}
          </div>
        </div>

        {/* Table Content Container */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 touch-auto">
          {filteredAgents.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 border border-dashed border-slate-200 rounded-xl space-y-3">
              <Search className="w-8 h-8 text-slate-300 mx-auto" />
              <h4 className="text-sm font-bold text-slate-700">
                {isIt ? 'Nessun modulo trovato' : isEs ? 'No se encontraron módulos' : isPt ? 'Nenhum módulo encontrado' : isSv ? 'Inga moduler hittades' : isDe ? 'Keine Module gefunden' : isFr ? 'Aucun module trouvé' : isPl ? 'Nie znaleziono modułów' : isEn ? 'No modules found' : 'Інструментів не знайдено'}
              </h4>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                {isIt ? 'Prova un altro termine di ricerca o reimposta il filtro delle categorie.' : isEs ? 'Pruebe con otro término de búsqueda o restablezca el filtro.' : isPt ? 'Tente outro termo de pesquisa ou reponha o filtro.' : isSv ? 'Försök med en annan sökterm eller återställ kategorifiltret.' : isDe ? 'Versuchen Sie einen anderen Suchbegriff oder eine andere Kategorie.' : isFr ? 'Essayez un autre mot-clé ou réinitialisez le filtre.' : isPl ? 'Spróbuj innego hasła wyszukiwania lub zresetuj filtr.' : isEn ? 'Try another search query or reset category filter.' : 'Спробуйте інший пошуковий запит або скиньте фільтр категорій.'}
              </p>
              <button
                onClick={() => { setSearchQuery(''); setSelectedCategory('ALL'); }}
                className="px-3.5 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-xs font-mono font-bold transition-all cursor-pointer"
              >
                {isIt ? 'Reimposta filtri' : isEs ? 'Restablecer filtros' : isPt ? 'Repor filtros' : isSv ? 'Återställ filter' : isDe ? 'Filter zurücksetzen' : isFr ? 'Réinitialiser les filtres' : isPl ? 'Zresetuj filtry' : isEn ? 'Reset Filters' : 'Скинути фільтри'}
              </button>
            </div>
          ) : (
            <div className="border border-slate-200 rounded-xl overflow-x-auto shadow-sm bg-white touch-auto max-h-[60vh] sm:max-h-none overflow-y-auto">
              <table className="w-full text-left border-collapse min-w-[780px]">
                <thead className="sticky top-0 z-20 bg-slate-100 shadow-sm">
                  <tr className="text-slate-700 font-bold uppercase text-[10px] font-mono border-b border-slate-200 tracking-wider">
                    <th className="p-4 min-w-[180px] bg-slate-100">{isIt ? 'Modulo Specialistico' : isEs ? 'Módulo Especializado' : isPt ? 'Módulo Especializado' : isSv ? 'Specialiserad Modul' : isDe ? 'Fachmodul' : isFr ? 'Module Spécialisé' : isPl ? 'Moduł Ekspercki' : isEn ? 'Specialized Module' : 'Спеціалізований модуль'}</th>
                    <th className="p-4 min-w-[240px] bg-slate-100">{isIt ? 'Descrizione & Funzionalità' : isEs ? 'Descripción & Alcance' : isPt ? 'Descrição & Âmbito' : isSv ? 'Beskrivning & Omfattning' : isDe ? 'Beschreibung & Funktionsumfang' : isFr ? 'Description & Périmètre' : isPl ? 'Opis / Funkcjonalność' : isEn ? 'Description & Scope' : 'Короткий опис'}</th>
                    <th className="p-4 text-center min-w-[150px] bg-slate-100">{isIt ? 'Abbonamento (30 giorni) & Richieste' : isEs ? 'Suscripción (30 días) & Solicitudes' : isPt ? 'Subscrição (30 dias) & Pedidos' : isSv ? 'Abonnemang (30 dagar) & Förfrågningar' : isDe ? 'Abonnement (30 Tage) & Anfragen' : isFr ? 'Abonnement (30 jours) & requêtes' : isPl ? 'Abonament (30 dni) i zapytania' : isEn ? 'Subscription (30 days) & limits' : 'Підписка (30 днів) та запити'}</th>
                    <th className="p-4 text-center min-w-[130px] bg-slate-100">{isIt ? 'Costo per richiesta' : isEs ? 'Coste por solicitud' : isPt ? 'Custo por pedido' : isSv ? 'Kostnad per förfrågan' : isDe ? 'Kosten pro Anfrage' : isFr ? 'Coût par requête' : isPl ? 'Koszt 1 zapytania' : isEn ? 'Cost per request' : 'Вартість 1 запиту'}</th>
                    <th className="p-4 text-center min-w-[120px] bg-slate-100">{isIt ? 'Uso singolo' : isEs ? 'Uso único' : isPt ? 'Uso avulso' : isSv ? 'Enstaka körning' : isDe ? 'Einzelabruf' : isFr ? 'Lancement unique' : isPl ? 'Pojedynczy start' : isEn ? 'Single Run' : 'Разовий запуск'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {filteredAgents.map(agent => {
                    const pricing = pricingMap[agent.id];
                    const subPrice = pricing ? parseFloat(pricing.price as any) : 500;
                    const limit = pricing ? pricing.requests_limit : 30;
                    const singlePrice = pricing ? parseFloat(pricing.single_price as any) : Math.round((subPrice / (limit || 1)) * 1.5);
                    const pricePerReq = limit > 0 ? (subPrice / limit) : 0;

                    const agentName = getLocalizedAgentName(agent, (language as Language) || 'uk');
                    const agentDesc = getLocalizedAgentDesc(agent, (language as Language) || 'uk');
                    const agentCat = getLocalizedAgentCategory(agent, (language as Language) || 'uk');

                    return (
                      <tr key={agent.id} className="hover:bg-slate-50/80 transition-colors">
                        {/* Agent Name & Badge */}
                        <td className="p-4 align-top">
                          <div className="flex items-start gap-2.5">
                            <div className="p-2 bg-blue-50 text-[#1E3A8A] rounded-lg border border-blue-100 shrink-0 mt-0.5">
                              <Zap className="w-4 h-4" />
                            </div>
                            <div>
                              <span className="font-bold text-slate-900 block text-xs leading-snug">
                                {agentName}
                              </span>
                              <span className="inline-block mt-1 px-2 py-0.5 bg-slate-100 text-slate-500 text-[9px] font-mono font-bold rounded uppercase">
                                {agentCat}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Description */}
                        <td className="p-4 align-top">
                          <p className="text-[11px] text-slate-600 leading-relaxed">
                            {agentDesc}
                          </p>
                        </td>

                        {/* Subscription price & requests */}
                        <td className="p-4 align-top text-center">
                          <div className="inline-flex flex-col items-center justify-center p-2.5 bg-emerald-50/60 border border-emerald-100 rounded-lg min-w-[130px]">
                            <span className="font-black text-sm text-emerald-800 font-mono">
                              {formatPrice(subPrice)}
                            </span>
                            <span className="text-[10px] text-emerald-600 font-bold font-mono mt-0.5 flex items-center gap-1">
                              <Tag className="w-3 h-3" /> {limit} {isIt ? 'richieste' : isEs ? 'solicitudes' : isPt ? 'pedidos' : isSv ? 'förfrågningar' : isDe ? 'Anfragen' : isFr ? 'requêtes' : isPl ? 'zapytań' : isEn ? 'requests' : 'запитів'} / {isIt ? '30 giorni' : isEs ? '30 días' : isPt ? '30 dias' : isSv ? '30 dagar' : isDe ? '30 Tage' : isFr ? '30 jours' : isPl ? '30 dni' : isEn ? '30 days' : '30 дн.'}
                            </span>
                          </div>
                        </td>

                        {/* Price per 1 sub request */}
                        <td className="p-4 align-top text-center">
                          <div className="inline-flex flex-col items-center justify-center py-2 px-3 bg-slate-50 border border-slate-200/80 rounded-lg">
                            <span className="font-bold text-xs text-slate-800 font-mono">
                              {formatUnitRate(pricePerReq)}
                            </span>
                            <span className="text-[9px] text-slate-400 uppercase font-mono">
                              {isIt ? 'per richiesta' : isEs ? 'por solicitud' : isPt ? 'por pedido' : isSv ? 'per förfrågan' : isDe ? 'pro Anfrage' : isFr ? 'par requête' : isPl ? 'za 1 zapytanie' : isEn ? 'per 1 request' : 'за 1 запит'}
                            </span>
                          </div>
                        </td>

                        {/* Single run price */}
                        <td className="p-4 align-top text-center">
                          <div className="inline-flex flex-col items-center justify-center p-2.5 bg-blue-50/60 border border-blue-100 rounded-lg min-w-[100px]">
                            <span className="font-black text-sm text-[#1E3A8A] font-mono">
                              {formatPrice(singlePrice)}
                            </span>
                            <span className="text-[9px] text-blue-600 font-mono font-bold uppercase mt-0.5">
                              {isIt ? 'A consumo' : isEs ? 'Pago por uso' : isPt ? 'Avulso' : isSv ? 'Pay-per-run' : isDe ? 'Einzelabruf' : isFr ? 'À la demande' : isPl ? 'Pojedynczo' : isEn ? 'Pay-per-run' : 'Разовий запуск'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Payment info & Free trial banner */}
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center font-bold shrink-0">
                <CreditCard className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs font-bold font-mono uppercase">
                  {isIt ? 'Pagamenti Internazionali Visa / Mastercard (Apple Pay & Google Pay)'
                    : isEs ? 'Pagos Internacionales Visa / Mastercard (Apple Pay & Google Pay)'
                    : isPt ? 'Pagamentos Internacionais Visa / Mastercard (Apple Pay & Google Pay)'
                    : isSv ? 'Internationella betalningar med Visa / Mastercard (Apple Pay & Google Pay)'
                    : isDe ? 'Internationale Zahlungen mit Visa / Mastercard (Apple Pay & Google Pay)' 
                    : isFr ? 'Paiements Internationaux Visa / Mastercard (Apple Pay & Google Pay)' 
                    : isPl ? 'Płatności kartami Visa/Mastercard (Apple Pay & Google Pay)' 
                    : isEn ? 'Visa/Mastercard Worldwide Payments (Apple Pay & Google Pay)' 
                    : 'Міжнародні платежі Visa/Mastercard (Apple Pay / Google Pay)'}
                </h4>
                <p className="text-[11px] text-slate-600">
                  {isIt ? 'Incasso sicuro a livello globale tramite Monobank Acquiring con conversione automatica della valuta.'
                    : isEs ? 'Cobro seguro a nivel global a través de Monobank Acquiring con conversión automática de divisas.'
                    : isPt ? 'Pagamentos seguros globais através de Monobank Acquiring com conversão automática de moedas.'
                    : isSv ? 'Säker global betalningshantering via Monobank Acquiring med automatisk valutaväxling.'
                    : isDe ? 'Sichere Zahlungsannahme weltweit über Monobank Acquiring mit automatischer Währungsumrechnung.'
                    : isFr ? 'Paiements sécurisés depuis le monde entier via Monobank Acquiring avec conversion automatique des devises.'
                    : isPl ? 'Bezpieczne płatności z dowolnego kraju świata przez bramkę płatniczą z automatycznym przewalutowaniem.'
                    : isEn ? 'Secure worldwide checkout via Monobank Acquiring with automatic currency exchange.'
                    : 'Безпечний прийом оплат з усього світу через Monobank Acquiring з авто-конвертацією валют.'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white font-bold text-xs uppercase font-mono rounded-lg transition-all cursor-pointer shrink-0"
            >
              {isIt ? 'Chiudi & Inizia' : isEs ? 'Cerrar e Iniciar' : isPt ? 'Fechar & Começar' : isSv ? 'Stäng & Starta' : isDe ? 'Schließen & Starten' : isFr ? 'Fermer et commencer' : isPl ? 'Zamknij i rozpocznij' : isEn ? 'Close & Start' : 'Увійти в систему'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-slate-500 text-[10px] font-mono">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span>
              {isIt ? 'Garanzia di disponibilità 99,9% • Assistenza tecnica 24/7'
                : isEs ? 'Garantía de disponibilidad 99,9% • Soporte técnico 24/7'
                : isPt ? 'Garantia de disponibilidade 99,9% • Suporte técnico 24/7'
                : isSv ? '99,9% tillgänglighetsgaranti • Teknisk support 24/7'
                : isDe ? '99,9% Verfügbarkeitsgarantie • Technischer Support 24/7'
                : isFr ? 'Garantie de disponibilité 99,9% • Support technique 24/7'
                : isPl ? 'Gwarancja 99.9% dostępności • Wsparcie techniczne 24/7'
                : isEn ? '99.9% Uptime Guarantee • 24/7 Priority Support'
                : 'Гарантія безперебійної роботи • Безлімітна підтримка'}
            </span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold uppercase rounded transition-all cursor-pointer"
          >
            {isIt ? 'Chiudi' : isEs ? 'Cerrar' : isPt ? 'Fechar' : isSv ? 'Stäng' : isDe ? 'Schließen' : isFr ? 'Fermer' : isPl ? 'Zamknij' : isEn ? 'Close' : 'Закрити'}
          </button>
        </div>

      </div>
    </div>
  );
};
